import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { clientForPortalUser, portalDataForClient, contractSignatures } from "@/mocks/saas-data";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { fmtUsd } from "@/mocks/data";

export const Route = createFileRoute("/portal/contracts")({
  ssr: false,
  head: () => ({ meta: [{ title: "Portal — Contracts" }] }),
  component: Page,
});

function Page() {
  const user = useRequireRole(["client_portal"]);
  const [signedTick, setSignedTick] = useState(0);
  if (!user) return null;
  const client = clientForPortalUser(user.id);
  const contracts = client ? portalDataForClient(client.name).contracts : [];

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Contracts" description="View and e-sign your agreements." />
      <div className="space-y-3">
        {contracts.map((c) => {
          const sig = contractSignatures[c.id];
          return (
            <div key={c.id} className="rounded-xl bg-card p-5 ring-1 ring-border">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-medium">{c.title}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    Sent {c.sent} · {fmtUsd(c.value)}
                  </p>
                  {sig && (
                    <p className="mt-2 text-xs text-success">
                      Signed by {sig.name} on {new Date(sig.at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                  {c.status === "sent" && !sig && (
                    <SignDialog
                      contractId={c.id}
                      defaultName={client?.name ?? ""}
                      onSigned={() => setSignedTick((t) => t + 1)}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {contracts.length === 0 && (
          <p className="rounded-xl bg-card p-10 text-center text-sm text-muted-foreground ring-1 ring-border">
            No contracts yet.
          </p>
        )}
      </div>
      <span className="hidden">{signedTick}</span>
    </div>
  );
}

function SignDialog({ contractId, defaultName, onSigned }: { contractId: string; defaultName: string; onSigned: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">E-sign</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>E-sign contract</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Type your full legal name as signature</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
          <div className="rounded-md border border-border bg-surface-muted/30 p-4 text-center">
            <p className="font-serif text-2xl italic">{name || "—"}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            By signing you agree to the terms. A timestamp and your IP will be recorded.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            disabled={!name.trim()}
            onClick={() => {
              contractSignatures[contractId] = { name: name.trim(), at: new Date().toISOString() };
              toast.success("Contract signed");
              setOpen(false);
              onSigned();
            }}
          >
            Sign now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
